import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, ImageUp, MapPin, Pencil, Plus, Save, Upload, X } from "lucide-react";
import { api, type Hotel, type Location, type LocationType, type SubLocation } from "../api/client";

export function HotelsPage() {
  const queryClient = useQueryClient();
  const hotels = useQuery({ queryKey: ["hotels"], queryFn: () => api<Hotel[]>("/api/admin/hotels") });
  const locationTypes = useQuery({ queryKey: ["location-types"], queryFn: () => api<LocationType[]>("/api/admin/location-types") });
  const locations = useQuery({ queryKey: ["locations"], queryFn: () => api<Location[]>("/api/admin/locations") });
  const subLocations = useQuery({ queryKey: ["sub-locations"], queryFn: () => api<SubLocation[]>("/api/admin/sub-locations") });
  const [selectedHotelId, setSelectedHotelId] = useState<string | null>(null);
  const [editingSubLocationId, setEditingSubLocationId] = useState<string | null>(null);
  const selectedHotel = hotels.data?.find((hotel) => hotel.id === selectedHotelId) ?? hotels.data?.[0];
  const selectedLocations = (locations.data ?? []).filter((location) => location.hotelId === selectedHotel?.id);
  const selectedSubLocations = (subLocations.data ?? []).filter((subLocation) => subLocation.hotelId === selectedHotel?.id);

  const createHotel = useMutation({
    mutationFn: (body: { name: string; code: string; brandName?: string; googleReviewUrl?: string; placeId?: string; reviewPromptTranslations?: unknown; timezone: string }) => api<Hotel>("/api/admin/hotels", {
      method: "POST",
      body: JSON.stringify(body)
    }),
    onSuccess: (hotel) => {
      setSelectedHotelId(hotel.id);
      queryClient.invalidateQueries({ queryKey: ["hotels"] });
    }
  });

  const updateHotel = useMutation({
    mutationFn: (body: Partial<Hotel> & { id: string }) => api<Hotel>(`/api/admin/hotels/${body.id}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["hotels"] })
  });

  const uploadLogo = useMutation({
    mutationFn: ({ hotelId, file }: { hotelId: string; file: File }) => {
      const form = new FormData();
      form.append("file", file);
      return api(`/api/admin/hotels/${hotelId}/branding/logo`, {
        method: "POST",
        body: form
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["hotels"] })
  });

  const createLocation = useMutation({
    mutationFn: (body: unknown) => api("/api/admin/locations", {
      method: "POST",
      body: JSON.stringify(body)
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["locations"] })
  });
  const createSubLocation = useMutation({
    mutationFn: (body: unknown) => api<SubLocation>("/api/admin/sub-locations", {
      method: "POST",
      body: JSON.stringify(body)
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["sub-locations"] })
  });
  const updateSubLocation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) => api<SubLocation>(`/api/admin/sub-locations/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    }),
    onSuccess: () => {
      setEditingSubLocationId(null);
      queryClient.invalidateQueries({ queryKey: ["sub-locations"] });
    }
  });
  const updateLocation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) => api<Location>(`/api/admin/locations/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["locations"] })
  });
  const bulkImportLocations = useMutation({
    mutationFn: (rows: unknown[]) => api<{ count: number }>("/api/admin/locations/bulk-import", {
      method: "POST",
      body: JSON.stringify({ rows })
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["locations"] })
  });

  function submitCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    createHotel.mutate({
      name: String(form.get("name")),
      code: String(form.get("code")),
      brandName: String(form.get("brandName") || ""),
      googleReviewUrl: String(form.get("googleReviewUrl") || ""),
      placeId: String(form.get("placeId") || ""),
      reviewPromptTranslations: parseJsonObject(String(form.get("reviewPromptTranslations") || "{}")),
      timezone: String(form.get("timezone") || "Europe/Podgorica")
    });
    event.currentTarget.reset();
  }

  function submitLocation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedHotel) return;
    const form = new FormData(event.currentTarget);
    createLocation.mutate({
      hotelId: selectedHotel.id,
      locationTypeId: String(form.get("locationTypeId")),
      code: String(form.get("code")),
      name: String(form.get("name")),
      floor: String(form.get("floor") || ""),
      zone: String(form.get("zone") || ""),
      googleReviewUrl: String(form.get("googleReviewUrl") || ""),
      placeId: String(form.get("placeId") || ""),
      metadata: {}
    });
    event.currentTarget.reset();
  }

  function submitSubLocation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedHotel) return;
    const form = new FormData(event.currentTarget);
    const locationId = String(form.get("locationId"));
    if (!locationId) return;
    createSubLocation.mutate({
      hotelId: selectedHotel.id,
      locationId,
      name: String(form.get("name")),
      code: String(form.get("code")),
      type: String(form.get("type") || "unit"),
      googleReviewUrl: String(form.get("googleReviewUrl") || ""),
      placeId: String(form.get("placeId") || ""),
      status: "active"
    });
    event.currentTarget.reset();
  }

  function submitSubLocationUpdate(event: FormEvent<HTMLFormElement>, subLocation: SubLocation) {
    event.preventDefault();
    if (!selectedHotel) return;
    const form = new FormData(event.currentTarget);
    const locationId = String(form.get("locationId"));
    if (!locationId) return;
    updateSubLocation.mutate({
      id: subLocation.id,
      body: {
        hotelId: selectedHotel.id,
        locationId,
        name: String(form.get("name")),
        code: String(form.get("code")),
        type: String(form.get("type") || "unit"),
        googleReviewUrl: String(form.get("googleReviewUrl") || ""),
        placeId: String(form.get("placeId") || ""),
        status: String(form.get("status") || subLocation.status)
      }
    });
  }

  function submitBulkLocations(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedHotel) return;
    const form = new FormData(event.currentTarget);
    const raw = String(form.get("rows") || "[]");
    let rows: Array<Record<string, unknown>>;
    try {
      rows = JSON.parse(raw);
      if (!Array.isArray(rows)) return;
    } catch {
      return;
    }
    bulkImportLocations.mutate(rows.map((row: Record<string, unknown>) => ({
      hotelId: selectedHotel.id,
      locationTypeId: String(row.locationTypeId || row.typeId || ""),
      code: String(row.code || ""),
      name: String(row.name || ""),
      floor: row.floor ? String(row.floor) : undefined,
      zone: row.zone ? String(row.zone) : undefined,
      placeId: row.placeId ? String(row.placeId) : undefined,
      metadata: typeof row.metadata === "object" && row.metadata ? row.metadata : {},
      status: String(row.status || "active")
    })));
    event.currentTarget.reset();
  }

  function submitUpdate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedHotel) return;
    const form = new FormData(event.currentTarget);
    updateHotel.mutate({
      id: selectedHotel.id,
      name: String(form.get("name")),
      brandName: String(form.get("brandName") || ""),
      googleReviewUrl: String(form.get("googleReviewUrl") || ""),
      placeId: String(form.get("placeId") || ""),
      reviewPromptTranslations: parseJsonObject(String(form.get("reviewPromptTranslations") || "{}")),
      timezone: String(form.get("timezone") || "Europe/Podgorica")
    });
  }

  return (
    <div className="page-stack">
      <section className="tool-hero card object-hero">
        <div>
          <span className="panel-eyebrow left">Hotel Objects</span>
          <h2>Objekti, lokacije i logo za javnu anketu.</h2>
          <p>Svaki scoped nalog vidi samo objekte koje smije da odrzava.</p>
        </div>
        <div className="object-brand-card">
          <Building2 size={34} />
          <strong>{selectedHotel?.brandName || selectedHotel?.name || "Hotel brand"}</strong>
          <span>{selectedHotel?.code ?? "code"} - {selectedHotel?.timezone ?? "Europe/Podgorica"}</span>
        </div>
      </section>

      <div className="grid-two">
        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Objekti</h2>
              <p>Korisnik vidi samo objekte koje smije da odrzava.</p>
            </div>
          </div>
          <div className="hotel-list">
            {(hotels.data ?? []).map((hotel) => (
              <button key={hotel.id} className={selectedHotel?.id === hotel.id ? "hotel-card selected" : "hotel-card"} onClick={() => setSelectedHotelId(hotel.id)}>
                <Building2 size={18} />
                <span>
                  <strong>{hotel.name}</strong>
                  <small>{hotel.code} - {hotel.timezone}</small>
                  {hotel.placeId ? <small>Place ID: {hotel.placeId}</small> : null}
                </span>
              </button>
            ))}
          </div>
          <form className="compact-form" onSubmit={submitCreate}>
            <h3><Plus size={16} /> Novi objekat</h3>
            <input name="name" placeholder="Naziv objekta" required />
            <input name="code" placeholder="Kratki kod, npr. a" required />
            <input name="brandName" placeholder="Brand naziv za anketu" />
            <input name="googleReviewUrl" placeholder="Google Review URL, opciono" />
            <input name="placeId" placeholder="Google Place ID, opciono" />
            <p className="form-note">Unesi samo Place ID, ne cijeli Google URL. Npr. <code>ChIJ...</code></p>
            <textarea name="reviewPromptTranslations" rows={4} placeholder='{"en":{"title":"Happy with your stay?","message":"You can also leave a public Google review.","action":"Leave Google Review","dismiss":"Not now"},"sr-Latn":{"title":"Zadovoljni ste boravkom?"}}' />
            <input name="timezone" defaultValue="Europe/Podgorica" />
            <button type="submit"><Plus size={16} /> Dodaj objekat</button>
          </form>
        </section>

        <section className="panel card">
          <h2>Branding ankete</h2>
          {selectedHotel ? (
            <>
              <form className="compact-form" onSubmit={submitUpdate}>
                <input name="name" defaultValue={selectedHotel.name} />
                <input name="brandName" defaultValue={selectedHotel.brandName ?? ""} placeholder="Brand naziv" />
                <input name="googleReviewUrl" defaultValue={selectedHotel.googleReviewUrl ?? ""} placeholder="Google Review URL" />
                <input name="placeId" defaultValue={selectedHotel.placeId ?? ""} placeholder="Google Place ID" />
                {selectedHotel.placeId ? <a href={googleReviewLink(selectedHotel.placeId)} target="_blank" rel="noreferrer">Test Google review link</a> : <p className="form-note">Unesi samo Google Place ID, ne cijeli URL.</p>}
                <textarea name="reviewPromptTranslations" rows={5} defaultValue={JSON.stringify(selectedHotel.reviewPromptTranslations ?? {}, null, 2)} placeholder='{"en":{"title":"Happy with your stay?","message":"You can also leave a public Google review.","action":"Leave Google Review","dismiss":"Not now"}}' />
                <input name="timezone" defaultValue={selectedHotel.timezone} />
                <button type="submit"><Save size={16} /> Sacuvaj</button>
              </form>
              <div className="logo-box">
                {selectedHotel.logoAsset ? (
                  <img src={`/assets/${selectedHotel.logoAsset.storageKey}`} alt={selectedHotel.name} />
                ) : (
                  <span>Logo nije podesen</span>
                )}
              </div>
              <label className="upload-control">
                <ImageUp size={16} />
                <span>Upload logo PNG/JPG/WebP/SVG do 512 KB</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  onChange={(event) => {
                    const file = event.currentTarget.files?.[0];
                    if (file) uploadLogo.mutate({ hotelId: selectedHotel.id, file });
                  }}
                />
              </label>
              <form className="compact-form" onSubmit={submitLocation}>
                <h3><MapPin size={16} /> Nova lokacija u objektu</h3>
                <select name="locationTypeId" required>
                  {(locationTypes.data ?? []).map((type) => <option key={type.id} value={type.id}>{type.name} ({type.code})</option>)}
                </select>
                <input name="code" placeholder="Kod lokacije, npr. r" required />
                <input name="name" placeholder="Naziv, npr. Restoran" required />
                <input name="floor" placeholder="Sprat, opciono" />
                <input name="zone" placeholder="Zona, opciono" />
                <input name="googleReviewUrl" placeholder="Google Review URL za lokaciju, opciono" />
                <input name="placeId" placeholder="Google Place ID za lokaciju, opciono" />
                <p className="form-note">Ako lokacija nema Place ID, koristi se Place ID objekta.</p>
                <button type="submit"><Plus size={16} /> Dodaj lokaciju</button>
              </form>
              <form className="compact-form" onSubmit={submitSubLocation}>
                <h3><MapPin size={16} /> Nova sublokacija / QR tačka</h3>
                <select name="locationId" required>
                  {selectedLocations.map((location) => <option key={location.id} value={location.id}>{location.name} ({location.code})</option>)}
                </select>
                <input name="code" placeholder="Kratki kod, npr. r1, 305, lb" required pattern="[a-zA-Z0-9-]{1,8}" />
                <input name="name" placeholder="Naziv, npr. Sto 1 / Soba 305" required />
                <input name="type" defaultValue="unit" placeholder="Tip: room/table/beach/lobby" />
                <input name="googleReviewUrl" placeholder="Google Review URL, opciono" />
                <input name="placeId" placeholder="Google Place ID, opciono" />
                <p className="form-note">Najkonkretniji Place ID ima prioritet: sublokacija, pa lokacija, pa objekat.</p>
                <button type="submit"><Plus size={16} /> Dodaj sublokaciju</button>
              </form>
              <section className="compact-form">
                <h3><MapPin size={16} /> Lokacije</h3>
                <div className="recipient-list">
                  {selectedLocations.map((location) => (
                    <article className="recipient-card" key={location.id}>
                      <div>
                        <strong>{location.name}</strong>
                        <span>{location.code} - {location.locationType.name}</span>
                        <small>{location.status} {location.floor ? `- sprat ${location.floor}` : ""} {location.zone ? `- ${location.zone}` : ""}</small>
                        {location.placeId ? <small>Place ID: {location.placeId}</small> : null}
                      </div>
                      <div className="row-actions">
                        <button disabled={location.status === "active"} onClick={() => updateLocation.mutate({ id: location.id, body: { status: "active" } })}>Active</button>
                        <button disabled={location.status === "paused"} onClick={() => updateLocation.mutate({ id: location.id, body: { status: "paused" } })}>Pause</button>
                        <button disabled={location.status === "archived"} onClick={() => updateLocation.mutate({ id: location.id, body: { status: "archived" } })}>Archive</button>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
              <section className="compact-form">
                <h3><MapPin size={16} /> Sublokacije</h3>
                <div className="recipient-list">
                  {selectedSubLocations.map((subLocation) => (
                    <article className="recipient-card" key={subLocation.id}>
                      {editingSubLocationId === subLocation.id ? (
                        <form className="sub-location-edit-form" onSubmit={(event) => submitSubLocationUpdate(event, subLocation)}>
                          <div className="inline-form-grid">
                            <label>
                              <span>Lokacija</span>
                              <select name="locationId" defaultValue={subLocation.locationId} required>
                                {selectedLocations.map((location) => <option key={location.id} value={location.id}>{location.name} ({location.code})</option>)}
                              </select>
                            </label>
                            <label>
                              <span>Kod</span>
                              <input name="code" defaultValue={subLocation.code} required pattern="[a-zA-Z0-9-]{1,8}" />
                            </label>
                            <label>
                              <span>Naziv</span>
                              <input name="name" defaultValue={subLocation.name} required />
                            </label>
                            <label>
                              <span>Tip</span>
                              <input name="type" defaultValue={subLocation.type} placeholder="room/table/beach/lobby" />
                            </label>
                            <label>
                              <span>Google Review URL</span>
                              <input name="googleReviewUrl" defaultValue={subLocation.googleReviewUrl ?? ""} placeholder="Opciono" />
                            </label>
                            <label>
                              <span>Google Place ID</span>
                              <input name="placeId" defaultValue={subLocation.placeId ?? ""} placeholder="Opciono" />
                            </label>
                            <label>
                              <span>Status</span>
                              <select name="status" defaultValue={subLocation.status}>
                                <option value="active">active</option>
                                <option value="paused">paused</option>
                                <option value="archived">archived</option>
                              </select>
                            </label>
                          </div>
                          <div className="row-actions">
                            <button type="submit" disabled={updateSubLocation.isPending}><Save size={14} /> Sacuvaj</button>
                            <button type="button" onClick={() => setEditingSubLocationId(null)}><X size={14} /> Cancel</button>
                          </div>
                        </form>
                      ) : (
                        <>
                          <div>
                            <strong>{subLocation.name}</strong>
                            <span>{subLocation.location.name} / {subLocation.code} - {subLocation.type}</span>
                            <small>{subLocation.status}</small>
                            {subLocation.placeId ? <small>Place ID: {subLocation.placeId}</small> : null}
                          </div>
                          <div className="row-actions">
                            <button type="button" onClick={() => setEditingSubLocationId(subLocation.id)}><Pencil size={14} /> Edit</button>
                            <button type="button" disabled={subLocation.status === "active"} onClick={() => updateSubLocation.mutate({ id: subLocation.id, body: { status: "active" } })}>Active</button>
                            <button type="button" disabled={subLocation.status === "paused"} onClick={() => updateSubLocation.mutate({ id: subLocation.id, body: { status: "paused" } })}>Pause</button>
                            <button type="button" disabled={subLocation.status === "archived"} onClick={() => updateSubLocation.mutate({ id: subLocation.id, body: { status: "archived" } })}>Archive</button>
                          </div>
                        </>
                      )}
                    </article>
                  ))}
                </div>
              </section>
              <form className="compact-form" onSubmit={submitBulkLocations}>
                <h3><Upload size={16} /> Bulk import lokacija</h3>
                <textarea
                  name="rows"
                  rows={5}
                  placeholder='[{"locationTypeId":"uuid","code":"302","name":"Soba 302","floor":"3","zone":"A","placeId":"ChIJ..."}]'
                />
                <button type="submit"><Upload size={16} /> Import/update lokacije</button>
              </form>
            </>
          ) : (
            <p>Nema dostupnih objekata.</p>
          )}
        </section>
      </div>
    </div>
  );
}

function googleReviewLink(placeId: string) {
  return `https://search.google.com/local/writereview?placeid=${encodeURIComponent(placeId)}`;
}

function parseJsonObject(value: string) {
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}
